import { useState } from 'react';
import type { CartItem } from '@/entities/cart';
import { fetchShopifyProduct } from '../model/shopifyProductService';
import { createShopifyCheckout } from '../model/shopifyService';

interface UseShopifyCheckoutResult {
  redirectToCheckout: (items: CartItem[]) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export default function useShopifyCheckout(): UseShopifyCheckoutResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectToCheckout = async (items: CartItem[]) => {
    setIsLoading(true);
    setError(null);

    try {
      const shopifyItemsPromises = items.map(async (item) => {
        const baseSlug = item.slug.replace(/-(?:white|black)$/, '');
        const color = item.color || 'white';

        const shopifyProduct = await fetchShopifyProduct(baseSlug);

        if (!shopifyProduct) {
          console.error(`[Shopify Checkout] No product found for handle: ${baseSlug}`);
          return null;
        }

        const variant = shopifyProduct.variants.find((v) =>
          v.selectedOptions.some(
            (opt) =>
              opt.name.toLowerCase() === 'color' && opt.value.toLowerCase() === color.toLowerCase(),
          ),
        );

        if (!variant) {
          console.error(`[Shopify Checkout] No variant found for ${baseSlug} with color ${color}`);
          return null;
        }

        return {
          variantId: variant.id,
          quantity: item.quantity,
        };
      });

      const shopifyItems = (await Promise.all(shopifyItemsPromises)).filter(
        (item): item is { variantId: string; quantity: number } => item !== null,
      );

      if (shopifyItems.length === 0) {
        throw new Error('store.cart.errors.noValidProducts');
      }

      const { checkoutUrl } = await createShopifyCheckout(shopifyItems);

      window.location.href = checkoutUrl;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create checkout';
      setError(errorMessage);
      console.error('[Shopify Checkout] Error details:', {
        error: err,
        message: errorMessage,
        items: items.map((item) => ({
          slug: item.slug,
          color: item.color,
          quantity: item.quantity,
        })),
      });
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    redirectToCheckout,
    isLoading,
    error,
  };
}
